import React, { useEffect, useState } from 'react'
import { Tabs, Tab, Box, TabList, TabPanels, TabPanel, Text, useDisclosure } from '@chakra-ui/react'
import { InactiveScheduledTable, ActiveScheduledTable, transformTransfers } from './index'
import { connect } from 'react-redux'
import useGetSchedules from '../../api/useGetSchedules'
import { RootState } from '../../store'
import { AuthorizationState } from '@Types'
import { hasScheduleTransferReadPermissions } from '../../utils/permissionsHelper'
import { isEmpty } from 'lodash'
import { AxiosError } from 'axios'
import UpdateTransferScheduleModal from '../../components/UpdateTransferScheduleModal'
import { TransferDirection, TransferType } from '@Types'
import CancelTransferScheduleModal from '../../components/CancelScheduledTransfersModal'

export type SelectedTransfer = {
  id: string
  amount: number
  direction: TransferDirection
  schedule: string
  type: TransferType
}

interface Props {
  accountNumber?: string
  auth: AuthorizationState
}

function ScheduledTransfersComponent({ accountNumber, auth }: Props) {
  const hasReadPerm = hasScheduleTransferReadPermissions(auth?.authorization?.permissions)

  const { isOpen: isUpdateOpen, onOpen: onUpdateOpen, onClose: onUpdateClose } = useDisclosure()
  const { isOpen: isCancelOpen, onOpen: onCancelOpen, onClose: onCancelClose } = useDisclosure()

  const [selectedTransfer, setSelectedTransfer] = useState<SelectedTransfer>()
  const onEditTransferAmount = (transfer: SelectedTransfer) => {
    setSelectedTransfer(transfer)
    onUpdateOpen()
  }
  const onStartCancel = (transfer: SelectedTransfer) => {
    setSelectedTransfer(transfer)
    onCancelOpen()
  }

  const { refetch, data, error, isLoading } = useGetSchedules(accountNumber)
  const errorObj = { ...(error as AxiosError | null) }
  const activeTransfers = transformTransfers(
    data?.data.recurringTransfers,
    auth,
    onEditTransferAmount,
    onStartCancel,
    accountNumber
  )

  useEffect(() => {
    if (hasReadPerm) {
      refetch()
    }
  }, [accountNumber, refetch, hasReadPerm])

  return (
    <>
      <Box marginTop='5'>
        <Tabs variant='enclosed'>
          <TabList position='relative'>
            <Tab>Active</Tab>
            <Tab>Inactive</Tab>
          </TabList>
          <TabPanels>
            <TabPanel padding='0'>
              {!isLoading && errorObj?.response?.status && errorObj?.response?.status >= 500 && (
                <Box minHeight='5' padding='5'>
                  <Text> There was a problem retrieving this information. Please try again later. </Text>
                </Box>
              )}
              {!isLoading && isEmpty(errorObj) && (
                <ActiveScheduledTable
                  hasReadPerm={hasReadPerm}
                  isLoading={isLoading}
                  tableData={activeTransfers.active}
                />
              )}
            </TabPanel>
            <TabPanel padding='0'>
              {!isLoading && isEmpty(errorObj) && (
                <InactiveScheduledTable
                  hasReadPerm={hasReadPerm}
                  isLoading={isLoading}
                  tableData={activeTransfers.inActive}
                />
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
      {isUpdateOpen && (
        <UpdateTransferScheduleModal onClose={onUpdateClose} selectedTransfer={selectedTransfer!} refetch={refetch} />
      )}
      {isCancelOpen && (
        <CancelTransferScheduleModal onClose={onCancelClose} selectedTransfer={selectedTransfer!} refetch={refetch} />
      )}
    </>
  )
}

const mapStateToProps = (state: RootState) => {
  const legitAuthState = (state as any)?.APEX_LEGIT_AUTHORIZATION
  return {
    auth: {
      hasBeenAsked: legitAuthState?.status === 'APEX_LEGIT_AUTHORIZATION/LOADING',
      authorization: legitAuthState?.results
    }
  }
}

export default connect(mapStateToProps)(ScheduledTransfersComponent)
